import { useMemo } from 'react';
import {
  WorkflowEvent,
  WorkflowEventState,
  WorkflowEventTypes,
} from '@amzn/awscat-pattern-library-service-model';

import { extractUserAlias } from '../../api/UtilsApi';
import { useContentWorkflowEvents } from '../v2-hooks/useContentWorkflowEvents';
import { useLoadedDocumentHandle } from '../v2-hooks/useLoadedDocumentHandle';
import { workflowEventTypeDisplayNames } from '../utils/DisplayNames';

export interface ActivityEntryViewModel {
  readonly eventId: string;
  readonly actionLabel: string;
  readonly userId: string;
  readonly timestamp: string;
  readonly comment: string | null;
}

export interface ActivityPanelViewModel {
  readonly isLoading: boolean;
  readonly entries: readonly ActivityEntryViewModel[];
  /** True when the entries also cover the version that was unpublished, not just this version. */
  readonly isShowingUnpublishedVersion: boolean;
}

const DISPLAYED_EVENT_TYPES = new Set<WorkflowEventTypes>([
  WorkflowEventTypes.APPROVE,
  WorkflowEventTypes.DISAPPROVE,
  WorkflowEventTypes.LOCK,
  WorkflowEventTypes.PUBLISH,
  WorkflowEventTypes.REJECT,
  WorkflowEventTypes.UNPUBLISH,
  WorkflowEventTypes.SEND_FOR_REVIEW,
  WorkflowEventTypes.FINALIZE,
  WorkflowEventTypes.DISTRIBUTE,
  WorkflowEventTypes.REVOKE,
  WorkflowEventTypes.EDIT_CONTENT_TYPE,
  WorkflowEventTypes.RETRACT_REVIEW,
  WorkflowEventTypes.PREAUTHORING_APPROVE,
  WorkflowEventTypes.PREAUTHORING_REJECT,
  WorkflowEventTypes.PREAUTHORING_NEED_MORE_INFO,
  WorkflowEventTypes.NO_UPDATES_NEEDED,
  WorkflowEventTypes.UPDATES_NEEDED,
  WorkflowEventTypes.UNPUBLISH_RECOMMENDED,
  WorkflowEventTypes.CONFIRM_UNPUBLISH,
  WorkflowEventTypes.PROPOSAL_APPROVE,
  WorkflowEventTypes.START_QUALITY_CHECK,
  WorkflowEventTypes.SUBMIT_FOR_PUBLISHER_REVIEW,
  WorkflowEventTypes.CANCEL_QUALITY_CHECK,
  WorkflowEventTypes.PUBLISHER_REVIEW_APPROVE,
  WorkflowEventTypes.PUBLISHER_REVIEW_REJECT,
  WorkflowEventTypes.PUBLISHER_REVIEW_RESUBMIT,
]);

function isDisplayableEvent(event: WorkflowEvent): boolean {
  return event.state === WorkflowEventState.APPLIED && DISPLAYED_EVENT_TYPES.has(event.type);
}

function formatTimestamp(event: WorkflowEvent): string {
  return event.createdAt.toFormat("LLL d, yyyy 'at' h:mm a 'UTC'");
}

function toEntryViewModel(event: WorkflowEvent): ActivityEntryViewModel {
  return {
    eventId: event.eventId,
    actionLabel: workflowEventTypeDisplayNames.get(event.type) ?? event.type,
    userId: extractUserAlias(event.createdBy),
    timestamp: formatTimestamp(event),
    comment: event.comment || null,
  };
}

export function useActivityPanelViewModel(): ActivityPanelViewModel {
  const events = useContentWorkflowEvents();
  const isShowingUnpublishedVersion = useLoadedDocumentHandle()?.hasUnpublishedEvents ?? false;

  // When events arrive, filter to displayable types, map, and sort (most recent first).
  const entries = useMemo(() => {
    if (!events) return [];
    return [...events]
      .filter(isDisplayableEvent)
      .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
      .map(toEntryViewModel);
  }, [events]);

  return {
    isLoading: events === undefined,
    entries,
    isShowingUnpublishedVersion,
  };
}
